import type { TelemetrySnapshot } from "../types/telemetry";
import { telemetrySnapshot } from "../data/mockTelemetry";

const USGS_ALL_DAY_URL = "https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/all_day.geojson";
const ISS_NOW_URL = "https://api.wheretheiss.at/v1/satellites/25544";
const EONET_EVENT_URLS = {
  fire: "https://eonet.gsfc.nasa.gov/api/v3/events?status=open&category=wildfires&limit=24",
  typhoon: "https://eonet.gsfc.nasa.gov/api/v3/events?status=open&category=severeStorms&limit=12",
  volcano: "https://eonet.gsfc.nasa.gov/api/v3/events?status=open&category=volcanoes&limit=18",
};
const NOAA_AURORA_URL = "https://services.swpc.noaa.gov/json/ovation_aurora_latest.json";
const NOAA_KP_URL = "https://services.swpc.noaa.gov/products/noaa-planetary-k-index.json";
const EONET_CACHE_MS = 10 * 60 * 1000;
const NOAA_CACHE_MS = 10 * 60 * 1000;
const ISS_POSITION_STORAGE_KEY = "earth-live:last-known-iss-position";
let lastKnownIssPosition: IssPosition | null = loadStoredIssPosition();
let eonetCache: { events: EonetEvent[]; fetchedAt: number } | null = null;
let noaaAuroraCache: { fetchedAt: number; forecast: AuroraForecast } | null = null;

type UsgsEarthquakeFeature = {
  geometry?: {
    coordinates?: [number, number, number?];
  };
  properties?: {
    mag?: number | null;
    place?: string | null;
    time?: number | null;
  };
};

type UsgsEarthquakeResponse = {
  features?: UsgsEarthquakeFeature[];
};

export async function getEarthDashboardSnapshot(): Promise<TelemetrySnapshot> {
  const [usgsEvents, issPosition, eonetEvents, auroraForecast] = await Promise.all([
    getUsgsSeismicEvents(),
    getIssPosition(),
    getNasaEonetEvents(),
    getNoaaAuroraForecast(),
  ]);
  let snapshot = telemetrySnapshot;

  if (usgsEvents.status === "connected" && usgsEvents.events.length > 0) {
    snapshot = mergeUsgsEarthquakes(snapshot, usgsEvents.events);
  }

  if (eonetEvents.status === "connected" && eonetEvents.events.length > 0) {
    snapshot = mergeEonetEvents(snapshot, eonetEvents.events);
  }

  if (auroraForecast.status === "connected" && auroraForecast.forecast) {
    snapshot = mergeNoaaAurora(snapshot, auroraForecast.forecast);
  }

  if ((issPosition.status === "connected" || issPosition.status === "cached") && issPosition.position) {
    snapshot = mergeIssPosition(snapshot, issPosition.position);
  } else {
    snapshot = mergeIssPendingStatus(snapshot);
  }

  return snapshot;
}

export async function getNasaGibsLayers() {
  return {
    provider: "NASA GIBS",
    status: "not-connected",
    layers: [],
  };
}

export async function getNoaaAuroraForecast() {
  if (noaaAuroraCache && Date.now() - noaaAuroraCache.fetchedAt < NOAA_CACHE_MS) {
    return {
      provider: "NOAA SWPC",
      status: "connected",
      forecast: noaaAuroraCache.forecast,
    };
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 9000);

  try {
    const [auroraResponse, kpResponse] = await Promise.all([
      fetch(NOAA_AURORA_URL, {
        signal: controller.signal,
        headers: {
          Accept: "application/json",
        },
      }),
      fetch(NOAA_KP_URL, {
        signal: controller.signal,
        headers: {
          Accept: "application/json",
        },
      }),
    ]);

    if (!auroraResponse.ok || !kpResponse.ok) {
      throw new Error(`NOAA request failed: aurora=${auroraResponse.status}, kp=${kpResponse.status}`);
    }

    const auroraData = (await auroraResponse.json()) as NoaaAuroraResponse;
    const kpData = (await kpResponse.json()) as NoaaKpResponse;
    const forecast = normalizeNoaaAuroraForecast(auroraData, kpData);

    if (!forecast) {
      throw new Error("NOAA response was missing aurora forecast data");
    }

    noaaAuroraCache = {
      fetchedAt: Date.now(),
      forecast,
    };

    return {
      provider: "NOAA SWPC",
      status: "connected",
      forecast,
    };
  } catch (error) {
    console.warn("NOAA aurora forecast unavailable. Keeping mock aurora data.", error);
  } finally {
    window.clearTimeout(timeoutId);
  }

  if (noaaAuroraCache) {
    return {
      provider: "NOAA SWPC",
      status: "connected",
      forecast: noaaAuroraCache.forecast,
    };
  }

  return {
    provider: "NOAA SWPC",
    status: "not-connected",
    forecast: null as AuroraForecast | null,
  };
}

export async function getNasaEonetEvents() {
  if (eonetCache && Date.now() - eonetCache.fetchedAt < EONET_CACHE_MS) {
    return {
      provider: "NASA EONET",
      status: "connected",
      events: eonetCache.events,
    };
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 9000);

  try {
    const responses = await Promise.all(
      Object.entries(EONET_EVENT_URLS).map(async ([kind, url]) => {
        const response = await fetch(url, {
          signal: controller.signal,
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`EONET ${kind} request failed: ${response.status}`);
        }

        return response.json() as Promise<EonetApiResponse>;
      }),
    );

    const events = responses
      .flatMap((response) => response.events ?? [])
      .map(normalizeEonetEvent)
      .filter((event): event is EonetEvent => event !== null)
      .sort((a, b) => b.time - a.time);

    eonetCache = {
      events,
      fetchedAt: Date.now(),
    };

    return {
      provider: "NASA EONET",
      status: "connected",
      events,
    };
  } catch (error) {
    console.warn("NASA EONET feed unavailable. Keeping mock natural event data.", error);
  } finally {
    window.clearTimeout(timeoutId);
  }

  if (eonetCache) {
    return {
      provider: "NASA EONET",
      status: "connected",
      events: eonetCache.events,
    };
  }

  return {
    provider: "NASA EONET",
    status: "not-connected",
    events: [] as EonetEvent[],
  };
}

export async function getUsgsSeismicEvents() {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 7000);

  try {
    const response = await fetch(USGS_ALL_DAY_URL, {
      signal: controller.signal,
      headers: {
        Accept: "application/geo+json, application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`USGS request failed: ${response.status}`);
    }

    const data = (await response.json()) as UsgsEarthquakeResponse;
    const events = (data.features ?? [])
      .map(normalizeUsgsFeature)
      .filter((event): event is UsgsEarthquakeEvent => event !== null)
      .sort((a, b) => b.time - a.time);

    return {
      provider: "USGS",
      status: "connected",
      events,
    };
  } catch (error) {
    console.warn("USGS earthquake feed unavailable. Falling back to mock data.", error);
  } finally {
    window.clearTimeout(timeoutId);
  }

  return {
    provider: "USGS",
    status: "not-connected",
    events: [] as UsgsEarthquakeEvent[],
  };
}

export async function getIssPosition() {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 12_000);

  try {
    const response = await fetch(ISS_NOW_URL, {
      signal: controller.signal,
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`ISS request failed: ${response.status}`);
    }

    const data = (await response.json()) as IssApiResponse;
    const position = normalizeIssResponse(data);

    if (!position) {
      throw new Error("ISS response was missing coordinates");
    }
    rememberIssPosition(position);

    return {
      provider: "Where the ISS at?",
      status: "connected",
      position,
    };
  } catch (error) {
    console.warn("ISS feed unavailable. Falling back to mock data.", error);
  } finally {
    window.clearTimeout(timeoutId);
  }

  if (lastKnownIssPosition) {
    return {
      provider: "Where the ISS at?",
      status: "cached",
      position: lastKnownIssPosition,
    };
  }

  return {
    provider: "ISS API",
    status: "not-connected",
    position: null as IssPosition | null,
  };
}

type IssApiResponse = {
  altitude?: number;
  latitude?: number;
  longitude?: number;
  timestamp?: number;
  velocity?: number;
  visibility?: string;
};

type IssPosition = {
  altitudeKm: number;
  lat: number;
  lon: number;
  timestamp: number;
  velocityKmh: number;
  visibility: "daylight" | "eclipsed" | "visible" | "unknown";
};

type EonetApiResponse = {
  events?: EonetApiEvent[];
};

type EonetApiEvent = {
  categories?: {
    id?: string;
    title?: string;
  }[];
  geometry?: {
    coordinates?: unknown;
    date?: string;
    magnitudeUnit?: string;
    magnitudeValue?: number;
    type?: string;
  }[];
  id?: string;
  link?: string;
  title?: string;
};

type EonetEvent = {
  category: "fire" | "typhoon" | "volcano";
  id: string;
  lat: number;
  lon: number;
  magnitudeText: string | null;
  place: string;
  time: number;
  title: string;
};

type EonetMapEvent = EonetEvent & {
  count: number;
};

type NoaaAuroraResponse = {
  "Forecast Time"?: string;
  "Observation Time"?: string;
  coordinates?: unknown;
};

type NoaaKpResponse = {
  Kp?: number;
  time_tag?: string;
}[];

type AuroraPoint = {
  lat: number;
  lon: number;
  value: number;
};

type AuroraForecast = {
  forecastTime: number;
  kp: number | null;
  northMax: AuroraPoint;
  southMax: AuroraPoint;
};

type UsgsEarthquakeEvent = {
  depthKm: number | null;
  lat: number;
  lon: number;
  magnitude: number | null;
  place: string;
  time: number;
};

function rememberIssPosition(position: IssPosition) {
  lastKnownIssPosition = position;

  try {
    window.localStorage.setItem(ISS_POSITION_STORAGE_KEY, JSON.stringify(position));
  } catch {
    // Local storage is a convenience cache; the live API remains the source of truth.
  }
}

function loadStoredIssPosition() {
  try {
    const rawValue = window.localStorage.getItem(ISS_POSITION_STORAGE_KEY);
    if (!rawValue) return null;
    const value = JSON.parse(rawValue) as Partial<IssPosition>;

    if (
      typeof value.lat !== "number" ||
      typeof value.lon !== "number" ||
      typeof value.velocityKmh !== "number" ||
      typeof value.altitudeKm !== "number" ||
      typeof value.timestamp !== "number"
    ) {
      return null;
    }

    return {
      altitudeKm: value.altitudeKm,
      lat: value.lat,
      lon: value.lon,
      timestamp: value.timestamp,
      velocityKmh: value.velocityKmh,
      visibility: value.visibility ?? "unknown",
    };
  } catch {
    return null;
  }
}

function normalizeIssResponse(data: IssApiResponse): IssPosition | null {
  if (
    typeof data.latitude !== "number" ||
    typeof data.longitude !== "number" ||
    typeof data.velocity !== "number" ||
    typeof data.altitude !== "number" ||
    typeof data.timestamp !== "number"
  ) {
    return null;
  }

  return {
    altitudeKm: data.altitude,
    lat: data.latitude,
    lon: normalizeLongitude(data.longitude),
    timestamp: data.timestamp * 1000,
    velocityKmh: data.velocity,
    visibility:
      data.visibility === "daylight" || data.visibility === "eclipsed" || data.visibility === "visible"
        ? data.visibility
        : "unknown",
  };
}

function normalizeUsgsFeature(feature: UsgsEarthquakeFeature): UsgsEarthquakeEvent | null {
  const [lon, lat, depthKm] = feature.geometry?.coordinates ?? [];
  const time = feature.properties?.time;

  if (typeof lat !== "number" || typeof lon !== "number" || typeof time !== "number") {
    return null;
  }

  return {
    depthKm: typeof depthKm === "number" ? depthKm : null,
    lat,
    lon,
    magnitude: typeof feature.properties?.mag === "number" ? feature.properties.mag : null,
    place: feature.properties?.place ?? "場所不明",
    time,
  };
}

function normalizeEonetEvent(event: EonetApiEvent): EonetEvent | null {
  const category = getEonetCategory(event);
  const geometry = getLatestPointGeometry(event.geometry ?? []);
  const coordinates = geometry?.coordinates;

  if (!category || !Array.isArray(coordinates)) {
    return null;
  }

  const [lon, lat] = coordinates;

  if (typeof lat !== "number" || typeof lon !== "number") {
    return null;
  }

  return {
    category,
    id: event.id ?? `${category}-${lat}-${lon}`,
    lat,
    lon: normalizeLongitude(lon),
    magnitudeText: formatEonetMagnitude(geometry?.magnitudeValue, geometry?.magnitudeUnit),
    place: formatEonetPlace(event.title ?? ""),
    time: geometry?.date ? new Date(geometry.date).getTime() : Date.now(),
    title: event.title ?? getEonetJapaneseTitle(category),
  };
}

function normalizeNoaaAuroraForecast(auroraData: NoaaAuroraResponse, kpData: NoaaKpResponse): AuroraForecast | null {
  if (!Array.isArray(auroraData.coordinates)) {
    return null;
  }

  const points = auroraData.coordinates
    .map((rawPoint) => {
      if (!Array.isArray(rawPoint)) return null;
      const [lon, lat, value] = rawPoint;

      if (typeof lat !== "number" || typeof lon !== "number" || typeof value !== "number") {
        return null;
      }

      return {
        lat,
        lon: normalizeLongitude(lon),
        value,
      };
    })
    .filter((point): point is AuroraPoint => point !== null);

  if (points.length === 0) {
    return null;
  }

  const northernPoints = points.filter((point) => point.lat >= 50);
  const southernPoints = points.filter((point) => point.lat <= -50);
  const latestKp = [...kpData]
    .filter((item) => typeof item.Kp === "number")
    .sort((a, b) => new Date(b.time_tag ?? 0).getTime() - new Date(a.time_tag ?? 0).getTime())[0];

  return {
    forecastTime: auroraData["Forecast Time"] ? new Date(auroraData["Forecast Time"]).getTime() : Date.now(),
    kp: typeof latestKp?.Kp === "number" ? latestKp.Kp : null,
    northMax: pickStrongestAuroraPoint(northernPoints.length > 0 ? northernPoints : points),
    southMax: pickStrongestAuroraPoint(southernPoints.length > 0 ? southernPoints : points),
  };
}

function pickStrongestAuroraPoint(points: AuroraPoint[]) {
  return points.reduce((bestPoint, point) => (point.value > bestPoint.value ? point : bestPoint), points[0]);
}

function mergeNoaaAurora(snapshot: TelemetrySnapshot, forecast: AuroraForecast): TelemetrySnapshot {
  const auroraLevel = getAuroraLevel(forecast.northMax.value, forecast.kp);
  const southLevel = getAuroraLevel(forecast.southMax.value, forecast.kp);
  const kpText = forecast.kp == null ? "確認中" : forecast.kp.toFixed(1);
  const phenomena = snapshot.phenomena.map((item) => {
    if (item.kind !== "aurora") return item;

    return {
      ...item,
      dataStatus: "live" as const,
      message: auroraLevel.message,
      stats: [
        { label: "北の空", value: auroraLevel.label },
        { label: "南の空", value: southLevel.label },
        { label: "太陽の元気度", value: kpText },
        { label: "更新", value: formatEventTimeJst(forecast.forecastTime) },
      ],
      detail: `NOAAの30分予報では、北側の一番強い場所は${formatAuroraArea(forecast.northMax)}あたりです。`,
      source: "取得元: NOAA SWPC。30分オーロラ予報とKp指数を認証なしの公開JSONから取得。",
    };
  });

  const globeObservations = [
    ...snapshot.globeObservations.filter((item) => item.title !== "オーロラ"),
    {
      dataStatus: "live" as const,
      title: "オーロラ",
      note: `${formatAuroraArea(forecast.northMax)} / ${auroraLevel.label}`,
      lat: forecast.northMax.lat,
      lon: forecast.northMax.lon,
      attachment: "geo" as const,
      tone: "green" as const,
    },
    {
      dataStatus: "live" as const,
      title: "オーロラ",
      note: `南の空 / ${southLevel.label}`,
      lat: forecast.southMax.lat,
      lon: forecast.southMax.lon,
      attachment: "geo" as const,
      tone: "green" as const,
    },
  ];

  const globeFeed = [
    `オーロラ: NOAA 30分予報 / 北の空 ${auroraLevel.label} / 太陽の元気度${kpText}`,
    ...snapshot.globeFeed.filter((item) => !item.startsWith("オーロラ:")),
  ];

  const liveReport = [
    `NOAAの30分予報では、北の空のオーロラは${auroraLevel.label}です。`,
    ...snapshot.liveReport.filter((item) => !item.includes("オーロラ")),
  ];

  return {
    ...snapshot,
    updatedAt: appendUpdateLabel(snapshot.updatedAt, "NOAA"),
    phenomena,
    globeObservations,
    globeFeed,
    liveReport,
  };
}

function mergeEonetEvents(snapshot: TelemetrySnapshot, events: EonetEvent[]): TelemetrySnapshot {
  const fireEvents = events.filter((event) => event.category === "fire");
  const volcanoEvents = events.filter((event) => event.category === "volcano");
  const typhoonEvents = events.filter((event) => event.category === "typhoon");
  const liveKinds = new Set<EonetEvent["category"]>();

  if (fireEvents.length > 0) liveKinds.add("fire");
  if (volcanoEvents.length > 0) liveKinds.add("volcano");
  if (typhoonEvents.length > 0) liveKinds.add("typhoon");

  const phenomena = snapshot.phenomena.map((item) => {
    if (item.kind === "fire" && fireEvents.length > 0) {
      const latest = fireEvents[0];

      return {
        ...item,
        dataStatus: "live" as const,
        message: `NASAの地図では、${latest.place}などに火災の印があります。`,
        stats: [
          { label: "表示", value: `${Math.min(fireEvents.length, 10)}件` },
          { label: "最新", value: latest.place },
          { label: "更新", value: formatEventTimeJst(latest.time) },
          { label: "大きさ", value: latest.magnitudeText ?? "確認中" },
        ],
        detail: "開いている火災イベントを、見やすい数にしぼって地球へ置いています。",
        source: "取得元: NASA EONET。公開APIから火災イベントを認証なしで取得。",
      };
    }

    if (item.kind === "volcano" && volcanoEvents.length > 0) {
      const latest = volcanoEvents[0];

      return {
        ...item,
        dataStatus: "live" as const,
        message: `NASAの地図では、${latest.place}などの火山に関する観測を表示しています。`,
        stats: [
          { label: "表示", value: `${Math.min(volcanoEvents.length, 8)}件` },
          { label: "注目", value: latest.place },
          { label: "更新", value: formatEventTimeJst(latest.time) },
        ],
        detail: "EONETの火山カテゴリです。噴火が今起きていると断定する表示ではありません。",
        source: "取得元: NASA EONET。火山情報はSmithsonian Global Volcanism Programなどの公開データをもとにしています。",
      };
    }

    if (item.kind === "typhoon" && typhoonEvents.length > 0) {
      const latest = typhoonEvents[0];

      return {
        ...item,
        dataStatus: "live" as const,
        title: "嵐",
        message: `NASAの地図では、${latest.place}に強い嵐の印があります。`,
        stats: [
          { label: "表示", value: `${typhoonEvents.length}件` },
          { label: "名前", value: latest.place },
          { label: "更新", value: formatEventTimeJst(latest.time) },
        ],
        detail: "台風や熱帯低気圧など、大きな嵐のイベントを表示しています。",
        source: "取得元: NASA EONET。公開APIから強い嵐のイベントを認証なしで取得。",
      };
    }

    return item;
  });

  const fireMapEvents = clusterEonetEvents(fireEvents, 7).slice(0, 8);
  const volcanoMapEvents = clusterEonetEvents(volcanoEvents, 4).slice(0, 8);
  const typhoonMapEvents = typhoonEvents.map((event) => ({ ...event, count: 1 })).slice(0, 5);

  const eonetObservations = [
    ...fireMapEvents.map((event) => createEonetObservation(event)),
    ...volcanoMapEvents.map((event) => createEonetObservation(event)),
    ...typhoonMapEvents.map((event) => createEonetObservation(event)),
  ];

  const globeObservations = [
    ...snapshot.globeObservations.filter((item) => {
      if (liveKinds.has("fire") && item.title === "火災") return false;
      if (liveKinds.has("volcano") && item.title === "火山") return false;
      if (liveKinds.has("typhoon") && (item.title === "台風の芽" || item.title === "嵐")) return false;
      return true;
    }),
    ...eonetObservations,
  ];

  const globeFeed = [
    ...(fireEvents.length > 0
      ? [`火災: NASA EONETから${fireEvents.length}件 / ${fireEvents[0].place}などを表示中`]
      : []),
    ...(volcanoEvents.length > 0
      ? [`火山: NASA EONETから${volcanoEvents.length}件 / ${volcanoEvents[0].place}などを表示中`]
      : []),
    ...(typhoonEvents.length > 0
      ? [`嵐: NASA EONETから${typhoonEvents.length}件 / ${typhoonEvents[0].place}を表示中`]
      : []),
    ...snapshot.globeFeed.filter(
      (item) =>
        !item.startsWith("火災:") &&
        !item.startsWith("火山:") &&
        !item.startsWith("台風の芽:") &&
        !item.startsWith("嵐:"),
    ),
  ];

  const liveReport = [
    ...(fireEvents.length > 0 ? [`NASA EONETでは、${fireEvents[0].place}などに火災の印があります。`] : []),
    ...(volcanoEvents.length > 0
      ? [`NASA EONETでは、${volcanoEvents[0].place}など火山に関する観測を表示しています。噴火中とは限りません。`]
      : []),
    ...(typhoonEvents.length > 0 ? [`NASA EONETでは、${typhoonEvents[0].place}に強い嵐の印があります。`] : []),
    ...snapshot.liveReport.filter(
      (item) => !item.includes("火災") && !item.includes("火山") && !item.includes("台風"),
    ),
  ];

  return {
    ...snapshot,
    updatedAt: appendUpdateLabel(snapshot.updatedAt, "NASA"),
    phenomena,
    globeObservations,
    globeFeed,
    liveReport,
  };
}

function mergeIssPosition(snapshot: TelemetrySnapshot, position: IssPosition): TelemetrySnapshot {
  const area = formatIssArea(position);
  const visibleText = formatIssVisibility(position.visibility);
  const phenomena = snapshot.phenomena.map((item) => {
    if (item.kind !== "iss") return item;

    return {
      ...item,
      dataStatus: "live" as const,
      message: `ISSは現在${area}の上を飛んでいます。`,
      stats: [
        { label: "場所", value: area },
        { label: "速度", value: `${Math.round(position.velocityKmh).toLocaleString("ja-JP")}km/h` },
        { label: "高さ", value: `約${Math.round(position.altitudeKm)}km` },
        { label: "見え方", value: visibleText },
      ],
      detail: `位置は${formatEventTimeJst(position.timestamp)}時点です。日本から見えるかは、次に観測地点を指定して計算します。`,
      source: "取得元: Where the ISS at?。ISSの現在位置、速度、高度を認証なしで取得。",
    };
  });

  const globeObservations = [
    ...snapshot.globeObservations.filter((item) => item.attachment !== "iss"),
    {
      dataStatus: "live" as const,
      title: "ISS",
      note: `${area} / ${Math.round(position.velocityKmh).toLocaleString("ja-JP")}km/h`,
      lat: position.lat,
      lon: position.lon,
      attachment: "iss" as const,
      tone: "amber" as const,
    },
  ];

  const globeFeed = [
    `ISS: 現在${area}の上 / 速度${Math.round(position.velocityKmh).toLocaleString("ja-JP")}km/h / 高さ約${Math.round(position.altitudeKm)}km`,
    ...snapshot.globeFeed.filter((item) => !item.startsWith("ISS:")),
  ];

  const liveReport = [
    ...snapshot.liveReport.filter((item) => !item.includes("ISS")),
    `ISSは現在${area}の上を飛んでいます。条件が合えば、夕方や明け方に光る点として見えることがあります。`,
  ];

  return {
    ...snapshot,
    updatedAt: appendUpdateLabel(snapshot.updatedAt, "ISS"),
    phenomena,
    globeObservations,
    globeFeed,
    liveReport,
  };
}

function mergeIssPendingStatus(snapshot: TelemetrySnapshot): TelemetrySnapshot {
  const phenomena = snapshot.phenomena.map((item) => {
    if (item.kind !== "iss") return item;

    return {
      ...item,
      dataStatus: "live" as const,
      message: "ISSの現在位置を確認中です。",
      stats: [
        { label: "状態", value: "接続中" },
        { label: "更新", value: "取得でき次第表示" },
      ],
      detail: "ISSは本データ接続済みの項目です。通信が不安定な時は、位置を無理にモックへ戻さず待機します。",
      source: "取得元: Where the ISS at?。ISSの現在位置、速度、高度を認証なしで取得。",
    };
  });

  return {
    ...snapshot,
    phenomena,
    globeObservations: snapshot.globeObservations.filter((item) => item.attachment !== "iss"),
    globeFeed: snapshot.globeFeed.filter((item) => !item.startsWith("ISS:")),
    liveReport: snapshot.liveReport.filter((item) => !item.includes("ISS")),
  };
}

function mergeUsgsEarthquakes(snapshot: TelemetrySnapshot, events: UsgsEarthquakeEvent[]): TelemetrySnapshot {
  const japanCount = countEventsInArea(events, { minLat: 24, maxLat: 46, minLon: 122, maxLon: 153 });
  const philippinesCount = countEventsInArea(events, { minLat: 4, maxLat: 22, minLon: 116, maxLon: 128 });
  const chileCount = countEventsInArea(events, { minLat: -56, maxLat: -17, minLon: -78, maxLon: -66 });
  const totalCount = events.length;
  const visibleEvents = pickVisibleEarthquakes(events);
  const latestVisible = visibleEvents[0];
  const japanVisible = visibleEvents.find((event) => isInJapanArea(event.lat, event.lon));
  const quakeMessage =
    latestVisible
      ? `地図には${formatReadablePlace(latestVisible)}など、目立つ揺れを表示しています。`
      : totalCount >= 180
      ? "今日の地震は多めです。"
      : totalCount >= 80
        ? "今日の地震はいつもくらいです。"
        : "今日の地震は少なめです。";

  const phenomena = snapshot.phenomena.map((item) => {
    if (item.kind !== "quake") return item;

    return {
      ...item,
      dataStatus: "live" as const,
      message: quakeMessage,
      stats: [
        { label: "日本周辺", value: `${japanCount}件` },
        { label: "フィリピン周辺", value: `${philippinesCount}件` },
        { label: "チリ周辺", value: `${chileCount}件` },
        { label: "地図表示", value: `${visibleEvents.length}件` },
        {
          label: "最新",
          value: latestVisible
            ? `${formatReadablePlace(latestVisible)} M${formatMagnitude(latestVisible.magnitude)}`
            : "なし",
        },
        {
          label: "日本近く",
          value: japanVisible
            ? `${formatReadablePlace(japanVisible)} M${formatMagnitude(japanVisible.magnitude)}`
            : "大きめはなし",
        },
        { label: "過去24時間 合計", value: `${totalCount}件` },
      ],
      detail: latestVisible
        ? `基準は直近6時間。日本周辺はM4.0以上、その他はM4.5以上を表示しています。`
        : "直近6時間で大きめの揺れは少なめです。",
      source: "取得元: USGS Earthquake GeoJSON。過去24時間の合計と、直近6時間のM4.5以上を中心に公開フィードから取得。",
    };
  });

  const nonQuakeObservations = snapshot.globeObservations.filter((item) => item.title !== "地震");
  const quakeObservations = visibleEvents.map((event) => ({
    dataStatus: "live" as const,
    title: "地震",
    note: `${formatReadablePlace(event)} M${formatMagnitude(event.magnitude)} / ${formatEventTimeJst(event.time)}`,
    lat: event.lat,
    lon: event.lon,
    attachment: "geo" as const,
    tone: "rose" as const,
  }));

  const globeFeed = [
    `地震: USGSから過去24時間 ${totalCount}件 / 日本周辺${japanCount}件 / フィリピン周辺${philippinesCount}件 / チリ周辺${chileCount}件`,
    ...snapshot.globeFeed.filter((item) => !item.startsWith("地震:")),
  ];

  const liveReport = [
    latestVisible
      ? `USGSによると、直近では${formatEventTimeJst(latestVisible.time)}に${formatReadablePlace(latestVisible)}でM${formatMagnitude(latestVisible.magnitude)}の地震がありました。`
      : "USGSから直近の地震を取得しています。",
    ...snapshot.liveReport.filter((item) => !item.includes("地震")),
  ];

  return {
    ...snapshot,
    updatedAt: appendUpdateLabel(snapshot.updatedAt, "USGS"),
    phenomena,
    globeObservations: [...nonQuakeObservations, ...quakeObservations],
    globeFeed,
    liveReport,
  };
}

function pickVisibleEarthquakes(events: UsgsEarthquakeEvent[]) {
  const sixHoursAgo = Date.now() - 6 * 60 * 60 * 1000;
  const recent = events
    .filter((event) => event.time >= sixHoursAgo)
    .filter((event) => isDisplayEarthquake(event))
    .sort((a, b) => b.time - a.time || (b.magnitude ?? 0) - (a.magnitude ?? 0))
    .slice(0, 16);

  return recent;
}

function createEonetObservation(event: EonetMapEvent) {
  const title = getEonetJapaneseTitle(event.category);
  const note = event.category === "fire"
    ? event.count > 1
      ? `${event.place}に火災 ${event.count}件`
      : `${event.place}に熱い点`
    : event.category === "volcano"
      ? event.count > 1
        ? `${event.place}に火山観測 ${event.count}件`
        : `${event.place}を監視中`
      : `${event.place}で発達中`;

  return {
    dataStatus: "live" as const,
    title,
    note,
    lat: event.lat,
    lon: event.lon,
    attachment: "geo" as const,
    tone: getEonetTone(event.category),
  };
}

function clusterEonetEvents(events: EonetEvent[], distanceDegrees: number): EonetMapEvent[] {
  const clusters: EonetEvent[][] = [];

  events.forEach((event) => {
    const cluster = clusters.find((items) => {
      const center = getClusterCenter(items);
      return getCoordinateDistance(center.lat, center.lon, event.lat, event.lon) <= distanceDegrees;
    });

    if (cluster) {
      cluster.push(event);
    } else {
      clusters.push([event]);
    }
  });

  return clusters
    .map((items) => {
      const center = getClusterCenter(items);
      const latest = [...items].sort((a, b) => b.time - a.time)[0];
      const place = items.length > 1 ? formatMapRegion(center.lat, center.lon, latest.place) : latest.place;

      return {
        ...latest,
        count: items.length,
        lat: center.lat,
        lon: center.lon,
        place,
      };
    })
    .sort((a, b) => b.count - a.count || b.time - a.time);
}

function getClusterCenter(events: EonetEvent[]) {
  const total = events.reduce(
    (sum, event) => ({
      lat: sum.lat + event.lat,
      lon: sum.lon + event.lon,
    }),
    { lat: 0, lon: 0 },
  );

  return {
    lat: total.lat / events.length,
    lon: normalizeLongitude(total.lon / events.length),
  };
}

function getCoordinateDistance(latA: number, lonA: number, latB: number, lonB: number) {
  const lonDistance = Math.abs(normalizeLongitude(lonA - lonB));
  const latDistance = Math.abs(latA - latB);

  return Math.hypot(latDistance, lonDistance);
}

function getEonetCategory(event: EonetApiEvent): EonetEvent["category"] | null {
  const categoryIds = event.categories?.map((category) => category.id) ?? [];

  if (categoryIds.includes("wildfires")) return "fire";
  if (categoryIds.includes("volcanoes")) return "volcano";
  if (categoryIds.includes("severeStorms")) return "typhoon";

  return null;
}

function getLatestPointGeometry(geometryItems: NonNullable<EonetApiEvent["geometry"]>) {
  return [...geometryItems]
    .filter((geometry) => geometry.type === "Point" && Array.isArray(geometry.coordinates))
    .sort((a, b) => {
      const aTime = a.date ? new Date(a.date).getTime() : 0;
      const bTime = b.date ? new Date(b.date).getTime() : 0;
      return bTime - aTime;
    })[0];
}

function getEonetJapaneseTitle(category: EonetEvent["category"]) {
  if (category === "fire") return "火災";
  if (category === "volcano") return "火山";
  return "嵐";
}

function getEonetTone(category: EonetEvent["category"]) {
  if (category === "fire") return "rose" as const;
  if (category === "volcano") return "amber" as const;
  return "cyan" as const;
}

function formatEonetMagnitude(value?: number, unit?: string) {
  if (typeof value !== "number" || !unit) return null;

  if (unit === "acres") {
    return `${Math.round(value).toLocaleString("ja-JP")}エーカー`;
  }

  if (unit === "knots") {
    return `${Math.round(value).toLocaleString("ja-JP")}ノット`;
  }

  return `${Math.round(value).toLocaleString("ja-JP")} ${unit}`;
}

function formatEonetPlace(title: string) {
  const trimmedTitle = title.trim();
  const wildfireMatch = trimmedTitle.match(/^Wildfire\s+\d+\s+(.+)$/i);
  const wildfireTitle = wildfireMatch?.[1] ?? trimmedTitle;
  const volcanoTitle = wildfireTitle.replace(/\s+Volcano\b/i, "");
  const stormTitle = volcanoTitle.replace(/^Tropical\s+(Storm|Cyclone|Depression)\s+/i, "");
  const pieces = stormTitle.split(",").map((piece) => piece.trim()).filter(Boolean);
  const location = pieces.length >= 2 ? pieces.slice(-2).join("、") : pieces[0] ?? "場所不明";

  return translateEonetLocation(location);
}

function formatMapRegion(lat: number, lon: number, fallback: string) {
  if (lat >= 48 && lon >= -125 && lon <= -60) return "カナダ南部";
  if (lat >= 42 && lon >= -125 && lon <= -103) return "アメリカ北西部";
  if (lat >= 32 && lat < 42 && lon >= -125 && lon <= -112) return "アメリカ西部";
  if (lat >= 25 && lat <= 36 && lon >= -105 && lon <= -80) return "アメリカ南部";
  if (lat >= 24 && lat <= 50 && lon >= -130 && lon <= -65) return "北アメリカ";
  if (lat >= -56 && lat <= 13 && lon >= -82 && lon <= -35) return "南アメリカ";
  if (lat >= -45 && lat <= -10 && lon >= 112 && lon <= 155) return "オーストラリア";
  if (lat >= -12 && lat <= 8 && lon >= 95 && lon <= 142) return "東南アジア";
  if (lat >= 24 && lat <= 46 && lon >= 122 && lon <= 153) return "日本周辺";
  if (lat >= -35 && lat <= 38 && lon >= -20 && lon <= 55) return "アフリカ";
  if (lat >= 35 && lat <= 72 && lon >= -12 && lon <= 45) return "ヨーロッパ";

  return fallback;
}

function translateEonetLocation(location: string) {
  return location
    .replace(/\bKikai\b/g, "鬼界カルデラ周辺")
    .replace(/\bIvao Group\b/g, "イワオ群")
    .replace(/\bBezymianny\b/g, "ベズイミアニ火山")
    .replace(/\bMayon\b/g, "マヨン火山")
    .replace(/\bOregon\b/g, "オレゴン")
    .replace(/\bCalifornia\b/g, "カリフォルニア")
    .replace(/\bWashington\b/g, "ワシントン")
    .replace(/\bArizona\b/g, "アリゾナ")
    .replace(/\bNevada\b/g, "ネバダ")
    .replace(/\bUtah\b/g, "ユタ")
    .replace(/\bIdaho\b/g, "アイダホ")
    .replace(/\bMontana\b/g, "モンタナ")
    .replace(/\bCanada\b/g, "カナダ")
    .replace(/\bChile\b/g, "チリ")
    .replace(/\bPeru\b/g, "ペルー")
    .replace(/\bIndonesia\b/g, "インドネシア")
    .replace(/\bPhilippines\b/g, "フィリピン")
    .replace(/\bRussia\b/g, "ロシア")
    .replace(/\bMexico\b/g, "メキシコ")
    .replace(/\bGuatemala\b/g, "グアテマラ")
    .replace(/\bIceland\b/g, "アイスランド")
    .replace(/\bJapan\b/g, "日本")
    .replace(/\bUnited States\b/g, "アメリカ")
    .replace(/\bUSA\b/g, "アメリカ")
    .replace(/\bPacific\b/g, "太平洋")
    .replace(/\bAtlantic\b/g, "大西洋");
}

function isDisplayEarthquake(event: UsgsEarthquakeEvent) {
  const magnitude = event.magnitude ?? 0;

  if (isInJapanArea(event.lat, event.lon)) {
    return magnitude >= 4;
  }

  return magnitude >= 4.5;
}

function formatIssArea(position: IssPosition) {
  const { lat, lon } = position;

  if (lat >= 24 && lat <= 46 && lon >= 122 && lon <= 153) return "日本周辺";
  if (lat >= 5 && lat <= 22 && lon >= 116 && lon <= 128) return "フィリピン周辺";
  if (lat >= -12 && lat <= 8 && lon >= 95 && lon <= 142) return "東南アジア周辺";
  if (lat >= -45 && lat <= 30 && lon >= 35 && lon <= 110) return "インド洋";
  if (lat >= -60 && lat <= 35 && lon >= -70 && lon <= 20) return "大西洋";
  if (lat >= -55 && lat <= 55 && (lon >= 110 || lon <= -70)) return "太平洋";
  if (lat >= -35 && lat <= 38 && lon >= -20 && lon <= 55) return "アフリカ周辺";
  if (lat >= 35 && lat <= 72 && lon >= -12 && lon <= 45) return "ヨーロッパ周辺";
  if (lat >= 5 && lat <= 72 && lon >= 45 && lon <= 145) return "アジア大陸上";
  if (lat >= 7 && lat <= 72 && lon >= -170 && lon <= -50) return "北アメリカ周辺";
  if (lat >= -56 && lat <= 13 && lon >= -82 && lon <= -35) return "南アメリカ周辺";
  if (lat >= -45 && lat <= -10 && lon >= 112 && lon <= 155) return "オーストラリア周辺";

  return lat >= 0 ? "北半球" : "南半球";
}

function formatIssVisibility(visibility: IssPosition["visibility"]) {
  if (visibility === "visible") return "見える可能性あり";
  if (visibility === "eclipsed") return "地球の影の中";
  if (visibility === "daylight") return "太陽に照らされています";
  return "確認中";
}

function getAuroraLevel(value: number, kp: number | null) {
  const combined = Math.max(value, (kp ?? 0) * 9);

  if (combined >= 75) {
    return {
      label: "とても光りやすい",
      message: "今日は高緯度の空でオーロラがかなり光りやすい予報です。",
    };
  }

  if (combined >= 45) {
    return {
      label: "光りやすい",
      message: "今日は北の空でオーロラが光りやすい予報です。",
    };
  }

  if (combined >= 20) {
    return {
      label: "少し光りやすい",
      message: "今日は北極の近くでオーロラが少し見えやすい予報です。",
    };
  }

  return {
    label: "おだやか",
    message: "今日はオーロラ活動はおだやかです。",
  };
}

function formatAuroraArea(point: AuroraPoint) {
  if (point.lat >= 66 && point.lon >= -170 && point.lon <= -50) return "北アメリカ北部";
  if (point.lat >= 66 && point.lon >= -45 && point.lon <= 45) return "北欧からグリーンランド";
  if (point.lat >= 66 && point.lon >= 45 && point.lon <= 170) return "シベリア北部";
  if (point.lat <= -66 && point.lon >= -90 && point.lon <= 20) return "南極大陸の大西洋側";
  if (point.lat <= -66 && point.lon >= 20 && point.lon <= 160) return "南極大陸のインド洋側";
  if (point.lat <= -66) return "南極大陸の太平洋側";

  return point.lat >= 0 ? "北極圏" : "南極圏";
}

function appendUpdateLabel(updatedAt: string, provider: string) {
  const providers = updatedAt.endsWith("更新") ? updatedAt.replace("更新", "").split("・").filter(Boolean) : [];

  if (!providers.includes(provider)) {
    providers.push(provider);
  }

  return providers.length > 0 ? `${providers.join("・")}更新` : `${provider}更新`;
}

function countEventsInArea(
  events: UsgsEarthquakeEvent[],
  area: { maxLat: number; maxLon: number; minLat: number; minLon: number },
) {
  return events.filter(
    (event) =>
      event.lat >= area.minLat &&
      event.lat <= area.maxLat &&
      event.lon >= area.minLon &&
      event.lon <= area.maxLon,
  ).length;
}

function formatMagnitude(magnitude: number | null) {
  return magnitude == null ? "?" : magnitude.toFixed(1);
}

function formatReadablePlace(event: UsgsEarthquakeEvent) {
  const regionalName = getRegionalEarthquakeName(event);
  const referencePlace = formatReferencePlace(event.place);

  if (regionalName) {
    return regionalName;
  }

  return referencePlace;
}

function getRegionalEarthquakeName(event: UsgsEarthquakeEvent) {
  const { lat, lon, place } = event;

  if (isInJapanArea(lat, lon)) {
    if (lat >= 39 && lat <= 41.8 && lon >= 141.4 && lon <= 145.5) return "三陸沖";
    if (lat >= 37 && lat < 39 && lon >= 141 && lon <= 145) return "宮城・福島沖";
    if (lat >= 35 && lat < 37 && lon >= 140.4 && lon <= 143.8) return "茨城・千葉沖";
    if (lat >= 30 && lat <= 34.8 && lon >= 128 && lon <= 133.8) return "九州南部周辺";
    if (lat >= 24 && lat <= 30 && lon >= 122 && lon <= 132) return "沖縄周辺";
    if (lat >= 42 && lon >= 140 && lon <= 147) return "北海道周辺";
    if (lon >= 138 && lon <= 142.5) return "本州周辺";
    return "日本周辺";
  }

  if (place.includes("South Sandwich Islands")) return "南サンドウィッチ諸島周辺";
  if (place.includes("Alaska")) return "アラスカ周辺";
  if (place.includes("Puerto Rico")) return "プエルトリコ周辺";
  if (place.includes("Russia") || place.includes("Kamchatsk")) return "カムチャツカ半島周辺";
  if (place.includes("Chile")) return "チリ周辺";
  if (place.includes("Hawaii")) return "ハワイ島周辺";
  if (place.includes("California") || place.endsWith(", CA")) return "カリフォルニア周辺";
  if (place.includes("Texas")) return "テキサス周辺";

  return null;
}

function isInJapanArea(lat: number, lon: number) {
  return lat >= 24 && lat <= 46 && lon >= 122 && lon <= 153;
}

function normalizeLongitude(lon: number) {
  return ((((lon + 180) % 360) + 360) % 360) - 180;
}

function formatReferencePlace(place: string) {
  const distanceMatch = place.match(/^([\d.]+)\s+km\s+([NSEW]{1,3})\s+of\s+(.+)$/);

  if (distanceMatch) {
    const [, distanceKm, direction, location] = distanceMatch;
    return `${translateLocationName(location)}から${formatDirection(direction)}へ約${Math.round(Number(distanceKm))}km`;
  }

  return translateLocationName(place);
}

function translateLocationName(location: string) {
  return location
    .replace(", Japan", "、日本")
    .replace(", Russia", "、ロシア")
    .replace(", Chile", "、チリ")
    .replace(", Alaska", "、アラスカ")
    .replace(", Puerto Rico", "、プエルトリコ")
    .replace(", Hawaii", "、ハワイ")
    .replace(", CA", "、カリフォルニア")
    .replace(", Texas", "、テキサス");
}

function formatDirection(direction: string) {
  const directions: Record<string, string> = {
    E: "東",
    ENE: "東北東",
    ESE: "東南東",
    N: "北",
    NE: "北東",
    NNE: "北北東",
    NNW: "北北西",
    NW: "北西",
    S: "南",
    SE: "南東",
    SSE: "南南東",
    SSW: "南南西",
    SW: "南西",
    W: "西",
    WNW: "西北西",
    WSW: "西南西",
  };

  return directions[direction] ?? direction;
}

function formatEventTimeJst(time: number) {
  return new Intl.DateTimeFormat("ja-JP", {
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    month: "numeric",
    timeZone: "Asia/Tokyo",
  }).format(new Date(time));
}
